import SlideoverModal from "src/components/SlideoverModal";
import { RootState } from "src/stores";
import { connect } from "react-redux";
import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import Lucide from "src/base-components/Lucide";
import { Get } from "src/services/http/fetch";
import LoadingIcon from "src/base-components/LoadingIcon";

interface ComponentProps {
  visible: boolean;
  setVisible: (payload: boolean) => void;
  currentUser?: any;
}

function Component(props: ComponentProps) {
  const { setVisible, visible } = props;

  const [filterValue, setFilterValue] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [past, setPast] = useState<any[]>([]);
  const [upcoming, setUpcoming] = useState<any[]>([]);
  const [pastPage, setPastPage] = useState(1);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [hasMorePast, setHasMorePast] = useState(false);
  const [hasMoreUpcoming, setHasMoreUpcoming] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const listRef = useRef<HTMLDivElement | null>(null);

  const formatDateTime = (dateStr?: string | null) => {
    try {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      const dd = d.getDate().toString().padStart(2, "0");
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const mon = months[d.getMonth()];
      const yyyy = d.getFullYear();
      let hh = d.getHours();
      const ampm = hh >= 12 ? "PM" : "AM";
      hh = hh % 12 || 12;
      const mm = d.getMinutes().toString().padStart(2, "0");
      return `${dd} ${mon}, ${yyyy} ${hh
        .toString()
        .padStart(2, "0")}:${mm} ${ampm}`;
    } catch {
      return dateStr || "";
    }
  };

  const handleFilterTabs = (value: any) => {
    try {
      setFilterValue(value);
    } catch (error) {
      console.log("Error setting filter value: ", error);
    }
  };

  const filterTabs = [
    {
      label: "Upcoming",
      value: 1,
    },
    {
      label: "Past",
      value: 2,
    },
  ];

  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    setError(null);
    Get("/reminders/queue")
      .then((res: any) => {
        setLoading(false);
        if (res && res.status) {
          const sortNewestFirst = (arr: any[]) =>
            (arr || []).slice().sort((a: any, b: any) => {
              const aDate = new Date(
                a.reminder?.created_at || a.created_at || 0
              ).getTime();
              const bDate = new Date(
                b.reminder?.created_at || b.created_at || 0
              ).getTime();
              return bDate - aDate; // newest first
            });

          setPast(sortNewestFirst(res.result?.past || []));
          setUpcoming(sortNewestFirst(res.result?.upcoming || []));

          const hasMoreP = !!res.result?.pagination?.has_more_past;
          const hasMoreU = !!res.result?.pagination?.has_more_upcoming;
          setHasMorePast(hasMoreP);
          setHasMoreUpcoming(hasMoreU);
          setPastPage(1);
          setUpcomingPage(1);
        } else {
          setError(res?.message || "Failed to fetch reminder queue");
        }
      })
      .catch((err: any) => {
        setLoading(false);
        setError(err?.toString?.() || "Failed to fetch reminder queue");
      });
  }, [visible]);

  const loadMore = async (type: "past" | "upcoming") => {
    try {
      if (loadingMore) return;
      if (type === "past" && !hasMorePast) return;
      if (type === "upcoming" && !hasMoreUpcoming) return;
      setLoadingMore(true);
      const nextPage = type === "past" ? pastPage + 1 : upcomingPage + 1;
      const url = `/reminders/queue?type=${type}&page=${nextPage}`;
      const res: any = await Get(url);
      if (res && res.status) {
        const newPast = Array.isArray(res.result?.past)
          ? res.result?.past
          : type === "past"
          ? res.result?.items || []
          : [];
        const newUpcoming = Array.isArray(res.result?.upcoming)
          ? res.result?.upcoming
          : type === "upcoming"
          ? res.result?.items || []
          : [];

        if (type === "past") {
          setPast((prev) => prev.concat(newPast));
          setPastPage(nextPage);
          const more = (res.result?.pagination?.has_more_past ??
            (newPast?.length || 0) > 0) as boolean;
          setHasMorePast(!!more);
        } else {
          setUpcoming((prev) => prev.concat(newUpcoming));
          setUpcomingPage(nextPage);
          const more = (res.result?.pagination?.has_more_upcoming ??
            (newUpcoming?.length || 0) > 0) as boolean;
          setHasMoreUpcoming(!!more);
        }
      }
    } catch (err) {
      // silent fail; keep scroll UX smooth
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    try {
      const el = e.currentTarget;
      const threshold = 100; // px from bottom
      if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
        const type = filterValue === 1 ? "upcoming" : "past";
        loadMore(type as any);
      }
    } catch {}
  };

  return (
    <>
      <SlideoverModal
        className="p-2"
        visible={visible}
        onClose={() => setVisible(false)}
        headerTitle="Reminder Queue"
      >
        <div>
          {error && (
            <div className="py-4 text-center text-sm text-red-600">{error}</div>
          )}

          <div className=" flex items-center justify-center">
            {loading ? (
              <div className="py-4 text-center text-sm text-slate-600">
                <LoadingIcon />
              </div>
            ) : upcoming.length + past.length === 0 ? (
              <div className="text-center text-slate-600">
                <div className="mb-2">
                  <Lucide
                    icon="Calendar"
                    className="w-10 h-10 mx-auto text-slate-400"
                  />
                </div>
                <div className="font-medium">No reminders</div>
                <div className="text-sm text-slate-500">
                  There are no reminders to show.
                </div>
              </div>
            ) : (
              <div className="w-full">
                <div className="flex justify-between w-full p-2 bg-[#F0F0F0] rounded-md">
                  {filterTabs.map((item: any, index: number) => (
                    <div
                      key={index}
                      className={`py-1 w-1/2 px-6 rounded-md cursor-pointer flex justify-center items-center ${
                        item.value === filterValue ? "bg-white" : ""
                      }`}
                      onClick={() => handleFilterTabs(item.value)}
                    >
                      {item.label}
                    </div>
                  ))}
                </div>

                <div
                  ref={listRef}
                  className="mt-5  overflow-y-auto"
                  onScroll={handleScroll}
                >
                  {(filterValue === 1 ? upcoming : past)?.map(
                    (item: any, idx: number) => (
                      <div
                        key={item.id || idx}
                        className="border-b border-gray-200  "
                      >
                        <div className="flex items-start px-3 py-3 rounded-xl hover:bg-slate-50/80 h-28">
                          <div className="mt-0.5">
                            <div className="relative overflow-hidden flex rounded-full w-11 h-11 image-fit  border border-slate-200/60 shadow-[inset_0_0_0_6px_#F3F4F6]">
                              <Lucide
                                icon="Clock3"
                                className={`m-auto w-6 h-6 ${
                                  item.status === "overdue"
                                    ? "text-red-500 animate-pulse"
                                    : "text-slate-600 animate-pulse"
                                }`}
                              />
                            </div>
                          </div>
                          <div className="sm:ml-5 flex-1 w-0">
                            <div className="flex items-start justify-between gap-3">
                              <div className="font-medium text-slate-800">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500 mr-2 align-middle" />
                                <span className="align-middle">
                                  {item.reminder?.title ||
                                    item?.title ||
                                    "Untitled"}
                                </span>
                              </div>
                              {item.reminder?.query_record?.query_no ? (
                                <div className="shrink-0">
                                  {item.reminder?.query_record?.query_no}
                                </div>
                              ) : null}
                            </div>
                            <div className="text-slate-500 mt-1 text-sm">
                              Type: {item.reminder?.reminder_type_text || "-"}
                            </div>
                            <div className="text-slate-600 mt-1 text-sm">
                              {formatDateTime(item.reminder?.remind_on)}
                            </div>
                            <div className="mt-1.5 text-xs text-slate-500">
                              Assigned To {props.currentUser?.name || "You"} By{" "}
                              {item.reminder?.created_by?.name || "System"}
                            </div>
                            <div className="ml-auto max-w-max flex flex-col items-end gap-2 -mt-[64px] ">
                              <button
                                onClick={() => {
                                  try {
                                    window.dispatchEvent(
                                      new CustomEvent("open-complete-modal", {
                                        detail: { item },
                                      })
                                    );
                                  } catch (err) {
                                    console.error(
                                      "Failed to dispatch open-complete-modal",
                                      err
                                    );
                                  }
                                  try {
                                    setVisible(false);
                                  } catch (err) {}
                                }}
                                className="rounded-full  w-[120px]  px-3 py-1 flex items-center cursor-pointer border border-slate-300 bg-slate-100/80 text-[#233580] hover:bg-slate-100 text-sm"
                              >
                                <span className="mr-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#233580] text-white shadow">
                                  <Lucide icon="Check" className="w-3 h-3" />
                                </span>
                                Complete
                              </button>
                              {filterValue === 1 && (
                                <button
                                  onClick={() => {
                                    try {
                                      window.dispatchEvent(
                                        new CustomEvent("open-snooze-modal", {
                                          detail: { item },
                                        })
                                      );
                                    } catch (err) {
                                      console.error(
                                        "Failed to dispatch open-snooze-modal",
                                        err
                                      );
                                    }
                                    try {
                                      setVisible(false);
                                    } catch (err) {}
                                  }}
                                  className="rounded-full w-[120px] px-3 py-1 flex items-center cursor-pointer border border-slate-300 bg-slate-100/80 text-[#ba1d1d] hover:bg-slate-100 text-sm"
                                >
                                  <span className="mr-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#ba1d1d] text-white shadow">
                                    <Lucide icon="Clock" className="w-3 h-3" />
                                  </span>
                                  Snooze
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  )}

                  <div className="py-3 flex items-center justify-center">
                    {loadingMore ? (
                      <div className="text-slate-500 text-sm flex items-center">
                        <LoadingIcon />
                        <span className="ml-2">Loading more…</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </SlideoverModal>
    </>
  );
}

// fetch handled inside Component when it becomes visible

const mapStateToProps = (state: RootState) => ({
  currentUser: (state as any)?.auth?.user,
});

const mapDispatchToProps = (dispatch: any) => ({});
export default connect(mapStateToProps, mapDispatchToProps)(Component);
